/**
 * The Phaser stage: backgrounds, character sprites, transitions, VFX.
 *
 * It owns no story logic — it subscribes to Runtime events and renders them.
 * Dialogue text, menus, and the backlog are React's job (see ../ui).
 */
import Phaser from 'phaser';
import type { Runtime, RuntimeEvent } from '@hon/core';
import { resolveAsset, type PackSource } from '@hon/core';
import type { PackManifest } from '@hon/schema';

// Portrait framing: slots are tighter than in landscape, since a 9:16 frame
// can only hold one character comfortably.
const SLOT_X: Record<string, number> = {
  'far-left': 0.22, left: 0.34, center: 0.5, right: 0.66, 'far-right': 0.78,
};

/** Character art fills nearly the full frame height, bleeding off the bottom. */
const SPRITE_HEIGHT_RATIO = 1.0;

export class StageScene extends Phaser.Scene {
  private bg?: Phaser.GameObjects.Image;
  private sprites = new Map<string, Phaser.GameObjects.Image>();
  private bgm: Phaser.Sound.BaseSound | undefined;
  private unsubscribe: (() => void) | undefined;

  constructor(
    private readonly runtime: Runtime,
    private readonly pack: PackManifest,
    private readonly source: PackSource,
  ) {
    super('stage');
  }

  preload(): void {
    // Manifest declares assets up front, so everything is prefetchable in one pass.
    for (const asset of this.pack.assets) {
      const url = resolveAsset(this.source, asset.path);
      if (asset.kind === 'image') this.load.image(asset.path, url);
      else if (asset.kind === 'audio') this.load.audio(asset.path, url);
    }
  }

  create(): void {
    this.drawPlaceholder();
    this.unsubscribe = this.runtime.on((e) => this.handle(e));
    this.scale.on('resize', () => this.layout());
    // Phaser.Scene has no destroy() to override — SHUTDOWN is the cleanup hook.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unsubscribe?.());
  }

  /**
   * Until real art lands, paint a dim gradient so the UI reads as a scene rather
   * than as a broken black screen. Any `bg` op covers this at depth 0.
   */
  private drawPlaceholder(): void {
    const { width, height } = this.scale;
    const g = this.add.graphics().setDepth(-1);
    g.fillGradientStyle(0x1a1220, 0x1a1220, 0x070509, 0x070509, 1);
    g.fillRect(0, 0, width, height);
    // Warm pool of light near the middle, roughly where a character stands.
    g.fillStyle(0x3a2418, 0.35);
    g.fillCircle(width * 0.5, height * 0.45, width * 0.55);
  }

  private handle(e: RuntimeEvent): void {
    switch (e.type) {
      case 'bg': return this.setBackground(e.image, e.frame, e.transition.ms);
      case 'show': return this.showSprite(e.sprite, e.at, e.emote, e.frame, e.transition.ms);
      case 'hide': return this.hideSprite(e.sprite, e.transition.ms);
      case 'bgm': return this.setBgm(e.track, e.fadeMs);
      case 'sfx': { this.sound.play(e.clip, { volume: e.volume }); return; }
      case 'fx': return this.playEffect(e.effect, e.ms, e.intensity);
      default: return; // say/choice/wait/scene/end are the UI layer's business
    }
  }

  private setBackground(
    key: string,
    frame: { x: number; y: number; w: number; h: number } | undefined,
    ms: number,
  ): void {
    const next = this.add.image(0, 0, key).setDepth(0);
    if (frame) next.setData('frame', frame);
    this.place(next);

    if (!this.bg || ms === 0) {
      this.bg?.destroy();
      this.bg = next;
      return;
    }
    next.setAlpha(0);
    const prev = this.bg;
    this.bg = next;
    this.tweens.add({
      targets: next, alpha: 1, duration: ms,
      onComplete: () => prev.destroy(),
    });
  }

  private showSprite(
    id: string,
    at: string,
    emote: string | undefined,
    frame: { x: number; y: number; w: number; h: number } | undefined,
    ms: number,
  ): void {
    const character = this.pack.characters.find((c) => c.id === id);
    const key = (emote && character?.portraits[emote]) ?? character?.portraits['default'] ?? id;

    /*
     * Art lags the script by design — the episode is written and staged long
     * before anyone draws it. Phaser answers a missing key with its magenta
     * placeholder texture, which reads as a bug rather than as unfinished work,
     * so skip the sprite instead. The scene still plays; it just has no one
     * standing in it yet.
     */
    if (!this.textures.exists(key)) {
      console.warn(`[hon] no art for "${key}" yet — showing the scene without it`);
      return;
    }

    this.sprites.get(id)?.destroy();

    const img = this.add.image(0, 0, key).setDepth(10);
    img.setData('at', at);
    if (frame) img.setData('frame', frame);
    this.place(img);

    this.sprites.set(id, img);
    if (ms > 0) {
      img.setAlpha(0);
      this.tweens.add({ targets: img, alpha: 1, duration: ms });
    }
  }

  private hideSprite(id: string, ms: number): void {
    const img = this.sprites.get(id);
    if (!img) return;
    this.sprites.delete(id);
    if (ms === 0) { img.destroy(); return; }
    this.tweens.add({ targets: img, alpha: 0, duration: ms, onComplete: () => img.destroy() });
  }

  private setBgm(track: string | null, fadeMs: number): void {
    const prev = this.bgm;
    if (prev) {
      this.tweens.add({
        targets: prev, volume: 0, duration: fadeMs,
        onComplete: () => prev.destroy(),
      });
    }
    if (!track) { this.bgm = undefined; return; }

    const next = this.sound.add(track, { loop: true, volume: 0 });
    next.play();
    this.tweens.add({ targets: next, volume: 0.6, duration: fadeMs });
    this.bgm = next;
  }

  private playEffect(effect: string, ms: number, intensity: number): void {
    const cam = this.cameras.main;
    switch (effect) {
      case 'shake': return void cam.shake(ms, 0.01 * intensity);
      case 'flash': return void cam.flash(ms, 255, 255, 255);
      case 'vignette': return void cam.fadeIn(ms);
      // rain / bloom / chromatic land here once the shader pipelines are authored
      default: return;
    }
  }

  private layout(): void {
    const { width, height } = this.scale;
    if (this.bg) this.place(this.bg);
    for (const img of this.sprites.values()) this.place(img);
  }

  /**
   * Put a layer where the storyboard puts it — a sprite, or the camera on a
   * background — falling back to its slot, or to filling the frame, when the
   * storyboard says nothing.
   *
   * The rectangle is a percentage of the board and may hang off either edge —
   * Marina draws characters 122% or 178% of the frame wide on purpose, so the
   * crop is part of the composition. Anchored top-left because that is the
   * corner Figma reports, and scaled on width so the aspect is the art's own.
   */
  private place(img: Phaser.GameObjects.Image): void {
    const { width, height } = this.scale;
    const frame = img.getData('frame') as { x: number; y: number; w: number; h: number } | undefined;

    if (!frame) {
      const at = img.getData('at') as string | undefined;
      /* A background with no camera rectangle fills the frame; a sprite with no
         rectangle stands in its slot. */
      if (at === undefined && img.depth === 0) {
        img.setOrigin(0.5, 0.5);
        img.setPosition(width / 2, height / 2);
        fitCover(img, width, height);
        return;
      }
      img.setOrigin(0.5, 1);
      img.setPosition((SLOT_X[at ?? 'center'] ?? 0.5) * width, height);
      img.setScale((height * SPRITE_HEIGHT_RATIO) / img.height);
      return;
    }

    img.setOrigin(0, 0);
    img.setPosition((frame.x / 100) * width, (frame.y / 100) * height);
    img.setScale(((frame.w / 100) * width) / img.width);
  }
}

function fitCover(img: Phaser.GameObjects.Image, w: number, h: number): void {
  img.setScale(Math.max(w / img.width, h / img.height));
}

